import React, { useEffect, useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'
import {
  getAdminSettings,
  listDirectoryUsers,
  REASONING_MODELS,
  saveAdminSettings,
  type AskEvansSettings,
  type DirectoryUser
} from '../../api/askEvansApi'
import { ASK_EVANS_FEATURE_EVENT } from './GlobalAskEvansFab'
import { AudiencePicker, isAudienceEmail, normalizeAudienceEmail } from './AudiencePicker'

const fieldClass =
  'mt-1 w-full rounded-lg border border-[#E2E5EA] px-4 py-2 font-sans text-sm text-blueprint-blue focus:border-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/30'

export function AIConfigurationPanel() {
  const [settings, setSettings] = useState<AskEvansSettings | null>(null)
  const [directory, setDirectory] = useState<DirectoryUser[]>([])
  const [directoryStatus, setDirectoryStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [draftEmail, setDraftEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    getAdminSettings()
      .then((loaded) => setSettings({ ...loaded, allowedEmails: loaded.allowedEmails || [] }))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load AI settings'))
    listDirectoryUsers()
      .then((loaded) => {
        setDirectory(loaded.users || [])
        setDirectoryStatus('ready')
      })
      .catch(() => setDirectoryStatus('error'))
  }, [])

  if (!settings) {
    return <p className="font-sans text-sm text-heritage-grey">{error || 'Loading AI configuration…'}</p>
  }

  const models = REASONING_MODELS.some((model) => model.id === settings.modelId)
    ? REASONING_MODELS
    : [...REASONING_MODELS, { id: settings.modelId, label: settings.modelId || 'Saved model' }]

  function withDraft(current: AskEvansSettings): AskEvansSettings | null {
    const pending = normalizeAudienceEmail(draftEmail)
    if (!pending) return current
    if (!isAudienceEmail(pending)) {
      setError('Enter a full email address, such as name@crce.com.')
      return null
    }
    if (current.allowedEmails.includes(pending)) return current
    return { ...current, allowedEmails: [...current.allowedEmails, pending] }
  }

  async function save() {
    if (!settings) return
    const payload = withDraft(settings)
    if (!payload) return
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const next = await saveAdminSettings(payload)
      const allowedEmails = Array.isArray(next.allowedEmails) ? next.allowedEmails : payload.allowedEmails
      setSettings({ ...next, allowedEmails })
      setDraftEmail('')
      setSaved(true)
      window.dispatchEvent(new Event(ASK_EVANS_FEATURE_EVENT))
      if (!allowedEmails.length && payload.enabled) {
        setError('Ask Evans is on, but nobody is on the list yet. Add an email and save again.')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-2xl" data-testid="ai-configuration">
      <div className="rounded-xl border border-[#E2E5EA] bg-white shadow-sm">
        <div className="border-b border-[#E2E5EA] px-6 py-4">
          <h2 className="font-heading text-[16px] font-semibold text-blueprint-blue">AI Provider Settings</h2>
        </div>
        <div className="space-y-5 px-6 py-5">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">AI Provider</p>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="ai-provider"
                checked={!settings.enabled}
                onChange={() => setSettings({ ...settings, enabled: false })}
              />
              None (disabled)
            </label>
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="ai-provider"
                checked={settings.enabled}
                onChange={() => setSettings({ ...settings, enabled: true })}
              />
              AWS Bedrock (execution role, no API key)
            </label>
          </div>

          {settings.enabled && (
            <>
              <label className="block text-sm font-medium text-gray-700">
                Bedrock region
                <input
                  aria-label="Bedrock region"
                  className={fieldClass}
                  value={settings.region}
                  onChange={(event) => setSettings({ ...settings, region: event.target.value })}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Reasoning model (Ask Evans)
                <select
                  aria-label="Bedrock model"
                  className={fieldClass}
                  value={settings.modelId}
                  onChange={(event) => setSettings({ ...settings, modelId: event.target.value })}
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                {(['standard', 'plus', 'max'] as const).map((name) => (
                  <label key={name} className="block text-sm font-medium capitalize text-gray-700">
                    {name} tokens
                    <input
                      aria-label={`${name} limit`}
                      className={fieldClass}
                      value={settings.limits[name]}
                      onChange={(event) =>
                        setSettings({ ...settings, limits: { ...settings.limits, [name]: event.target.value } })
                      }
                    />
                  </label>
                ))}
              </div>
            </>
          )}

          <div className="space-y-3 border-t border-[#E2E5EA] pt-5">
            <div>
              <h3 className="text-[13px] font-semibold text-blueprint-blue">AI features</h3>
              <p className="mt-1 text-[12px] text-heritage-grey">
                Turn Ask Evans off to stop it being offered to anybody, without waiting for a release.
              </p>
            </div>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded accent-blueprint-blue"
                checked={settings.enabled}
                onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })}
              />
              <span>
                <span className="block text-[13px] font-medium text-blueprint-blue">
                  Ask Evans <span className="font-normal text-heritage-grey">(reasoning model)</span>
                </span>
                <span className="block text-[12px] text-heritage-grey">
                  The Ask Evans chat assistant (floating panel and full page). When off, Ask Evans APIs are blocked.
                </span>
              </span>
            </label>
            {settings.enabled && (
              <AudiencePicker
                emails={settings.allowedEmails}
                directory={directory}
                directoryStatus={directoryStatus}
                draft={draftEmail}
                disabled={saving}
                onDraft={setDraftEmail}
                onChange={(allowedEmails) => setSettings({ ...settings, allowedEmails })}
                onInvalid={(message) => setError(message || null)}
              />
            )}
          </div>

          {error && <p className="text-sm text-readable-red">{error}</p>}
          {saved && <p className="text-sm text-galvanizing-green">AI configuration saved.</p>}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="inline-flex items-center gap-2 rounded-lg bg-hero-red px-4 py-2 text-sm font-medium text-white hover:bg-hero-red-60 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
